import { useRouter } from 'next/router'
import { ReactNode } from 'react'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import { ClipLoader } from 'react-spinners'

import { useFocusActionState } from 'src/providers/FocusActionProvider'
import { ModalType, useModal } from 'src/providers/ModalProvider'
import { useStepper } from 'src/providers/StepperProvider'

import { ButtonRebrand } from 'src/components/atoms/ButtonRebrand'
import { Divider } from 'src/components/atoms/Divider'
import { OnClickLink } from 'src/components/atoms/Link'
import { ParagraphSmaller } from 'src/components/atoms/Typography'

import { setActiveStepIndex } from 'src/utils/stepper'

export type StepperButtonsProps = {
  isSubmitDisabled?: boolean
  isOtherSubmitDisabled?: boolean
  processing?: boolean
  onContinueLater?: () => void
  continueLaterText?: string
  onSubmit?: () => void
  /**
   * Function to be executed on back.
   * @returns true if normal back behaviour should be ignored, otherwise goes back 1 step
   */
  onBack?: () => boolean | Promise<boolean>
  backText?: string
  submitText?: string
  submitPrompt?: string | ReactNode
  onCancel?: () => void
  cancelDestination?: string
  cancelPrompt?: string
}

export const StepperButtons = ({
  isOtherSubmitDisabled,
  isSubmitDisabled,
  processing,
  onContinueLater,
  continueLaterText = 'Save & continue later',
  onSubmit,
  onBack,
  backText = 'Back',
  submitText = 'Submit',
  submitPrompt,
  onCancel,
  cancelPrompt = '',
  cancelDestination = ''
}: StepperButtonsProps) => {
  const router = useRouter()
  const { step, steps, nextStep } = useStepper()
  const { openModal } = useModal()
  const { returnUrl } = useFocusActionState()

  const goBackEnabled = steps.indexOf(step) > 0

  const handleOnBack = async () => {
    const handleBackResult = onBack ? await onBack() : false
    if (handleBackResult) return
    const prevStepIndex = steps.indexOf(step) - 1
    setActiveStepIndex(router, steps.length, prevStepIndex)
  }

  const handleOnCancel = () => {
    openModal(ModalType.Cancel, {
      prompt: cancelPrompt,
      destination: cancelDestination || returnUrl,
      onCancel
    })
  }

  const handleSubmitClick = () => {
    if (onSubmit) {
      onSubmit()
    } else {
      nextStep()
    }
  }

  return (
    <>
      <Divider />
      <div className='flex flex-col gap-4'>
        <div className='flex flex-col-reverse md:flex-row justify-between gap-10'>
          <OnClickLink
            color='forestDeep'
            onClick={handleOnCancel}
            className='font-body-rebrand underline self-center md:self-auto'
          >
            Cancel
          </OnClickLink>
          <div className='text-right space-y-2'>
            <div className='flex flex-col-reverse md:flex-row gap-[10px]'>
              {goBackEnabled && (
                <ButtonRebrand onClick={handleOnBack} variant='outline'>
                  <MdChevronLeft size={20} />
                  {backText}
                </ButtonRebrand>
              )}
              {onContinueLater && (
                <ButtonRebrand
                  variant='outline'
                  disabled={isOtherSubmitDisabled}
                  onClick={onContinueLater}
                >
                  {continueLaterText}
                </ButtonRebrand>
              )}
              <ButtonRebrand
                variant='lime'
                disabled={isSubmitDisabled}
                onClick={handleSubmitClick}
              >
                {processing ? (
                  <>
                    <ClipLoader
                      className='mr-1'
                      size={16}
                      color='currentColor'
                    />
                    Processing...
                  </>
                ) : (
                  <>
                    {submitText}
                    <MdChevronRight size={20} />
                  </>
                )}
              </ButtonRebrand>
            </div>
          </div>
        </div>
        {submitPrompt ? (
          <div className='flex justify-end'>
            {typeof submitPrompt === 'string' ||
            submitPrompt instanceof String ? (
              <ParagraphSmaller className='mb-0! text-right'>
                {submitPrompt}
              </ParagraphSmaller>
            ) : (
              submitPrompt
            )}
          </div>
        ) : null}
      </div>
    </>
  )
}
